import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, QrCode, Wrench, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { generateReportPDF } from '../lib/pdf';
import EditEntityDialog from '../components/EditEntityDialog';
import DeleteButton from '../components/DeleteButton';
import BuildingFilter from '../components/BuildingFilter';
import TagsInput from '../components/TagsInput';
import FileUpload from '../components/FileUpload';

export default function Assets() {
  const { user } = useContext(AuthContext);
  const t = useT();
  const [assets, setAssets] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [scanning, setScanning] = useState(false);

  const [newAsset, setNewAsset] = useState({
    building_id: '',
    name: '',
    asset_type: 'HVAC',
    description: '',
    maintenance_frequency: 30,
    manufacturer: '',
    model_number: '',
    serial_number: '',
    notes: '',
    book_value: 0,
    image_url: '',
    tags: []
  });
  const [filterBuildingId, setFilterBuildingId] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (scanning) {
      const scanner = new Html5QrcodeScanner('qr-reader', {
        fps: 10,
        qrbox: 250
      });

      scanner.render(
        (decodedText) => {
          handleQRScan(decodedText);
          scanner.clear();
          setScanning(false);
        },
        (error) => {
          // Scan error, ignore
        }
      );

      return () => {
        scanner.clear();
      };
    }
  }, [scanning]);

  const fetchData = async () => {
    try {
      const [assetsRes, buildingsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/assets`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setAssets(assetsRes.data);
      setBuildings(buildingsRes.data);
      const myCompany = companiesRes.data.find(c => c.id === user?.company_id) || companiesRes.data[0];
      setCompany(myCompany);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const downloadAssetsPDF = () => {
    const buildingName = (id) => buildings.find(b => b.id === id)?.name || 'Unassigned';
    const byType = assets.reduce((acc, a) => {
      const t = a.asset_type || 'Other';
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});
    const byStatus = assets.reduce((acc, a) => {
      const s = a.status || 'operational';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});

    generateReportPDF({
      title: 'Asset Register',
      subtitle: `${assets.length} assets across ${buildings.length} building(s)`,
      company,
      sections: [
        {
          heading: 'Summary',
          head: ['Metric', 'Count'],
          rows: [
            ['Total Assets', String(assets.length)],
            ['Total Buildings', String(buildings.length)],
            ...Object.entries(byType).map(([k, v]) => [`Type: ${k}`, String(v)]),
            ...Object.entries(byStatus).map(([k, v]) => [`Status: ${k}`, String(v)]),
          ]
        },
        {
          heading: 'Asset Inventory',
          head: ['Asset Name', 'Type', 'Building', 'Status', 'Maint. Freq (days)'],
          rows: assets.map(a => [
            a.name || '-',
            a.asset_type || '-',
            buildingName(a.building_id),
            a.status || 'operational',
            String(a.maintenance_frequency || '-')
          ])
        }
      ],
      filename: `asset_register_${new Date().toISOString().split('T')[0]}.pdf`
    });
    toast.success('Asset register PDF generated');
  };

  const handleCreateAsset = async () => {
    try {
      await axios.post(`${API}/assets`, newAsset, { withCredentials: true });
      toast.success('Asset created successfully with QR code');
      setShowNewDialog(false);
      setNewAsset({
        building_id: '',
        name: '',
        asset_type: 'HVAC',
        description: '',
        maintenance_frequency: 30
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create asset');
    }
  };

  const handleQRScan = async (assetId) => {
    try {
      const response = await axios.get(`${API}/assets/${assetId}/qr`, { withCredentials: true });
      const asset = assets.find(a => a.id === assetId);
      if (asset) {
        setSelectedAsset({ ...asset, qr_code: response.data.qr_code });
        setShowQRDialog(true);
      }
    } catch (error) {
      toast.error('Asset not found');
    }
  };

  const viewQRCode = async (asset) => {
    try {
      const response = await axios.get(`${API}/assets/${asset.id}/qr`, { withCredentials: true });
      setSelectedAsset({ ...asset, qr_code: response.data.qr_code });
      setShowQRDialog(true);
    } catch (error) {
      toast.error('Failed to load QR code');
    }
  };

  return (
    <Layout>
      <div data-testid="assets-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('asset.title')}</h1>
            <p className="text-muted-foreground">{t('asset.subtitle')}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <BuildingFilter value={filterBuildingId} onChange={setFilterBuildingId} buildings={buildings} />
            <Button
              data-testid="download-assets-pdf"
              variant="outline"
              onClick={downloadAssetsPDF}
              disabled={!assets.length}
            >
              <Download className="w-4 h-4 mr-2" />
              {t('asset.downloadPDF')}
            </Button>
            <Button
              data-testid="scan-qr-btn"
              variant="outline"
              onClick={() => setScanning(!scanning)}
            >
              <QrCode className="w-4 h-4 mr-2" />
              {scanning ? t('asset.stopScan') : t('asset.scanQR')}
            </Button>
            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
              <DialogTrigger asChild>
                <Button data-testid="new-asset-btn" className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  New Asset
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Register New Asset</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Building</Label>
                    <Select
                      value={newAsset.building_id}
                      onValueChange={(value) => setNewAsset({ ...newAsset, building_id: value })}
                    >
                      <SelectTrigger data-testid="asset-building-select">
                        <SelectValue placeholder="Select building" />
                      </SelectTrigger>
                      <SelectContent>
                        {buildings.map(building => (
                          <SelectItem key={building.id} value={building.id}>
                            {building.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Asset Name</Label>
                    <Input
                      data-testid="asset-name-input"
                      value={newAsset.name}
                      onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                      placeholder="e.g., Main HVAC Unit"
                    />
                  </div>
                  <div>
                    <Label>Asset Type</Label>
                    <Select
                      value={newAsset.asset_type}
                      onValueChange={(value) => setNewAsset({ ...newAsset, asset_type: value })}
                    >
                      <SelectTrigger data-testid="asset-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HVAC">HVAC</SelectItem>
                        <SelectItem value="Elevator">Elevator</SelectItem>
                        <SelectItem value="Plumbing">Plumbing</SelectItem>
                        <SelectItem value="Electrical">Electrical</SelectItem>
                        <SelectItem value="Fire Safety">Fire Safety</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      data-testid="asset-description-input"
                      value={newAsset.description}
                      onChange={(e) => setNewAsset({ ...newAsset, description: e.target.value })}
                      placeholder="Additional details"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Maintenance Frequency (days)</Label>
                    <Input
                      data-testid="maintenance-frequency-input"
                      type="number"
                      value={newAsset.maintenance_frequency}
                      onChange={(e) => setNewAsset({ ...newAsset, maintenance_frequency: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Manufacturer</Label><Input data-testid="asset-manufacturer-input" value={newAsset.manufacturer} onChange={(e) => setNewAsset({ ...newAsset, manufacturer: e.target.value })} /></div>
                    <div><Label>Model #</Label><Input value={newAsset.model_number} onChange={(e) => setNewAsset({ ...newAsset, model_number: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Serial #</Label><Input value={newAsset.serial_number} onChange={(e) => setNewAsset({ ...newAsset, serial_number: e.target.value })} /></div>
                    <div><Label>Book Value (optional)</Label><Input data-testid="asset-book-value-input" type="number" value={newAsset.book_value} onChange={(e) => setNewAsset({ ...newAsset, book_value: parseFloat(e.target.value) || 0 })} /></div>
                  </div>
                  <div>
                    <Label>Asset Photo (optional) — upload or take a picture</Label>
                    <FileUpload
                      value={newAsset.image_url ? [newAsset.image_url] : []}
                      onChange={(urls) => setNewAsset({ ...newAsset, image_url: urls[urls.length - 1] || '' })}
                      maxFiles={1}
                      accept="image/*"
                      showCamera
                    />
                  </div>
                  <div>
                    <Label>Notes (warranty, manufacturer details, etc.)</Label>
                    <Textarea data-testid="asset-notes-input" value={newAsset.notes} onChange={(e) => setNewAsset({ ...newAsset, notes: e.target.value })} rows={2} />
                  </div>
                  <div>
                    <Label>Tags</Label>
                    <TagsInput value={newAsset.tags} onChange={(tags) => setNewAsset({ ...newAsset, tags })} testIdPrefix="asset-tag" />
                  </div>
                  <Button
                    data-testid="submit-asset-btn"
                    onClick={handleCreateAsset}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={!newAsset.building_id || !newAsset.name}
                  >
                    Create Asset
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* QR Scanner */}
        {scanning && (
          <Card className="p-6">
            <div id="qr-reader" className="w-full max-w-md mx-auto"></div>
          </Card>
        )}

        {/* Assets Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assets.filter(a => filterBuildingId === 'all' || a.building_id === filterBuildingId).map((asset) => (
              <Card
                key={asset.id}
                data-testid={`asset-card-${asset.id}`}
                className="p-6 hover:shadow-xl transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <Wrench className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      data-testid={`view-qr-${asset.id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => viewQRCode(asset)}
                    >
                      <QrCode className="w-4 h-4" />
                    </Button>
                    {['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role) && (
                      <>
                        <EditEntityDialog
                          item={asset}
                          endpoint="assets"
                          title="Edit Asset"
                          onSaved={fetchData}
                          fields={[
                            { key: 'name', label: 'Asset Name' },
                            { key: 'asset_type', label: 'Type', type: 'select', options: [
                              { value: 'HVAC', label: 'HVAC' },
                              { value: 'Electrical', label: 'Electrical' },
                              { value: 'Plumbing', label: 'Plumbing' },
                              { value: 'Fire Safety', label: 'Fire Safety' },
                              { value: 'Elevator', label: 'Elevator' },
                              { value: 'Generator', label: 'Generator' },
                              { value: 'Other', label: 'Other' },
                            ]},
                            { key: 'building_id', label: 'Building', type: 'select', options: buildings.map(b => ({ value: b.id, label: b.name })) },
                            { key: 'description', label: 'Description', type: 'textarea', rows: 2 },
                            { key: 'manufacturer', label: 'Manufacturer' },
                            { key: 'model_number', label: 'Model #' },
                            { key: 'serial_number', label: 'Serial #' },
                            { key: 'book_value', label: 'Book Value', type: 'number' },
                            { key: 'image_url', label: 'Asset Photo', type: 'image' },
                            { key: 'notes', label: 'Notes', type: 'textarea', rows: 3 },
                            { key: 'tags', label: 'Tags', type: 'tags' },
                            { key: 'status', label: 'Status', type: 'select', options: [
                              { value: 'operational', label: 'Operational' },
                              { value: 'under_maintenance', label: 'Under Maintenance' },
                              { value: 'broken', label: 'Broken' },
                              { value: 'retired', label: 'Retired' },
                            ]},
                            { key: 'maintenance_frequency', label: 'Maintenance Frequency (days)', type: 'number' },
                          ]}
                        />
                        <DeleteButton endpoint="assets" id={asset.id} label={`asset "${asset.name}"`} testId={`delete-asset-${asset.id}`} onDeleted={fetchData} />
                      </>
                    )}
                  </div>
                </div>
                {asset.image_url && (
                  <a href={asset.image_url} target="_blank" rel="noopener noreferrer" className="block mb-3 -mt-2 rounded-lg overflow-hidden border border-slate-200">
                    <img src={asset.image_url} alt={asset.name} data-testid={`asset-img-${asset.id}`} className="w-full h-32 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </a>
                )}
                <h3 className="text-lg font-bold text-foreground mb-2">{asset.name}</h3>
                <p className="text-sm text-muted-foreground mb-3">{asset.description}</p>
                {(asset.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {asset.tags.map((tg, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">{tg}</span>)}
                  </div>
                )}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Type:</span>
                    <span className="font-semibold text-foreground">{asset.asset_type}</span>
                  </div>
                  {asset.manufacturer && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Manufacturer:</span>
                      <span className="font-semibold text-foreground">{asset.manufacturer}</span>
                    </div>
                  )}
                  {asset.book_value > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Book Value:</span>
                      <span className="font-semibold text-foreground">{asset.book_value?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      asset.status === 'operational' ? 'bg-green-100 text-green-800' :
                      asset.status === 'under_maintenance' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {asset.status}
                    </span>
                  </div>
                  {asset.next_maintenance && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Next Maintenance:</span>
                      <span className="font-semibold text-foreground">
                        {new Date(asset.next_maintenance).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* QR Code Dialog */}
        <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{selectedAsset?.name} - QR Code</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center space-y-4 py-6">
              {selectedAsset?.qr_code && (
                <img
                  src={selectedAsset.qr_code}
                  alt="Asset QR Code"
                  className="w-64 h-64 border-4 border-slate-200 rounded-xl"
                />
              )}
              <p className="text-sm text-muted-foreground text-center">
                Scan this QR code to quickly access asset information
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}